<script setup lang="ts">
import { computed, ref } from "vue";
import { v4 as uuidv4 } from "uuid";
import InputLabel from "./InputLabel.vue";
import InfoIcon from "@/features/Tooltip/InfoIcon.vue";
import SelectArrowIcon from "./SelectArrowIcon.vue";

type CastType = "string" | "number" | ((v: string) => string | number | null);

const props = defineProps<{
  modelValue: string | number | null;
  label?: string;
  id?: string;
  placeholder?: string;
  tooltip?: string;
  disabled?: boolean;
  required?: boolean;
  errorMsg?: string;
  hasError?: boolean;
  width?: string;
  cast?: CastType;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
}>();

const localId = ref(uuidv4());
const inputId = computed(() => props.id ?? `select-${localId.value}`);
const styleWidth = computed(() => props.width ?? "100%");

function castValue(raw: string): string | number | null {
  if (raw === "__placeholder__") return null;

  const c = props.cast ?? "string";
  if (typeof c === "function") return c(raw);
  if (c === "number") {
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }
  return raw;
}

function onChange(e: Event) {
  const raw = (e.target as HTMLSelectElement).value;
  emit("update:modelValue", castValue(raw));
}

const selectedString = computed(() => {
  const v = props.modelValue;
  if (v === null || v === undefined || v === "") return props.placeholder ? "__placeholder__" : "";
  return String(v);
});
</script>

<template>
  <!-- style kept: width is an arbitrary CSS value passed as a prop (e.g. "50%", "200px") -->
  <!-- style kept: width is an arbitrary CSS value passed as a prop (e.g. "50%", "200px") -->
  <div
    class="flex flex-col"
    :style="{ width: styleWidth }">
    <!-- No mb-1: it made the select's field start 4px lower than a text or number input's, so a label
         and a select side by side in the same row had their boxes at different heights. Both of those
         put the label directly above the field, and two out of three is the pattern to match. -->
    <!-- h-6 for the same reason as InputLabel's own row: this wrapper holds the label beside an InfoIcon
         whose hit area is 24px, so without a fixed height a field with a tooltip sits 8px lower than one
         without, and two of them in a row do not line up. Both must be pinned, because the icon lives
         inside the label in SelectInputWithKeyValue and beside it here. 24px is the icon's own box, and it
         leaves 4px under the label text so it is not flush against the control. -->
    <div
      v-if="label"
      class="flex h-6 items-center">
      <InputLabel
        :id="inputId"
        :label="label"
        :required="required" />
      <InfoIcon
        v-if="tooltip"
        :tooltip="tooltip" />
    </div>

    <div class="relative inline-block">
      <select
        :id="inputId"
        class="focus-input rounded-8 w-full appearance-none border py-2 pr-10 pl-3 text-sm leading-normal"
        :disabled="disabled"
        :value="selectedString"
        :aria-required="required ? true : undefined"
        :aria-invalid="hasError ? true : undefined"
        :aria-describedby="hasError && errorMsg ? `${inputId}-error` : undefined"
        :class="[
          disabled ? 'text-type-muted bg-slate-200' : 'bg-slate-0',
          hasError ? 'border-app-red-500' : 'border-slate-300',
          !selectedString && placeholder ? 'text-type-muted' : '',
        ]"
        @change="onChange">
        <option
          v-if="placeholder"
          value="__placeholder__"
          disabled
          hidden>
          {{ placeholder }}
        </option>

        <slot />
      </select>
      <!-- Arrow -->
      <SelectArrowIcon />
    </div>
    <p
      v-if="hasError && errorMsg"
      :id="`${inputId}-error`"
      class="text-app-red-700 mt-1 text-xs">
      {{ errorMsg }}
    </p>
  </div>
</template>
